import { z } from 'zod';
export const deviceStatusSchema = z.enum(['online', 'offline', 'unknown']);
export const locationTypeSchema = z.enum(['gps', 'wifi', 'lbs', 'unknown']);
export const deviceAssignmentTypeSchema = z.enum(['none', 'person', 'vehicle']);
/** Métadonnées privées à MyTag, jamais envoyées au fournisseur. */
export const localDeviceMetadataSchema = z.object({
    deviceNum: z.string(),
    localName: z.string().nullable(),
    notes: z.string(),
    assignmentType: deviceAssignmentTypeSchema,
    assignmentLabel: z.string().nullable(),
    archivedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** Point de localisation normalisé (indépendant du format brut Vernal). */
export const locationPointSchema = z.object({
    deviceNum: z.string(),
    latitude: z.number(),
    longitude: z.number(),
    accuracy: z.number().nullable().default(null),
    battery: z.number().nullable().default(null),
    locationType: locationTypeSchema.default('unknown'),
    /** ISO 8601 UTC */
    timestamp: z.string(),
    /** Valeur fournisseur originale, si présente. */
    datePublished: z.string().nullable().optional(),
    address: z.string().nullable().default(null),
    speed: z.number().nullable().default(null),
    direction: z.number().nullable().default(null),
});
/** Device normalisé. */
export const deviceSchema = z.object({
    deviceNum: z.string(),
    name: z.string(),
    status: deviceStatusSchema.default('unknown'),
    battery: z.number().nullable().default(null),
    latitude: z.number().nullable().default(null),
    longitude: z.number().nullable().default(null),
    accuracy: z.number().nullable().default(null),
    locationType: locationTypeSchema.default('unknown'),
    /** ISO 8601 UTC de la dernière position connue */
    lastUpdate: z.string().nullable().default(null),
    imei: z.string().nullable().default(null),
    iccid: z.string().nullable().default(null),
    model: z.string().nullable().default(null),
    mac: z.string().nullable().default(null),
    /** ID utilisateur amont associé au device, normalisé en chaîne. */
    userId: z.string().nullable().default(null),
    /** true si le deviceNum fait partie des devices de test connus */
    known: z.boolean().default(false),
    /** Couche locale MyTag ; le nom et les champs fournisseur ci-dessus restent inchangés. */
    localMetadata: localDeviceMetadataSchema.nullable().optional(),
    /**
     * Repli historique : dernière position connue via getLocationList, présente
     * uniquement quand getDeviceList ne renvoie aucune position en cache pour cet
     * appareil (latitude/longitude/lastUpdate ci-dessus restent null). Ce n'est
     * JAMAIS une lecture temps réel — l'UI doit toujours l'afficher comme telle.
     */
    lastHistoricalLocation: locationPointSchema.nullable().optional(),
});
export const sessionInfoSchema = z.object({
    sessionId: z.string(),
    username: z.string(),
    userId: z.string().nullable(),
    mock: z.boolean(),
    createdAt: z.string(),
    expiresAt: z.string(),
    /** Empreinte masquée du token upstream, jamais le token lui-même. */
    tokenPreview: z.string().nullable(),
});
export const dashboardSummarySchema = z.object({
    totalDevices: z.number(),
    onlineDevices: z.number(),
    offlineDevices: z.number(),
    /** Devices pour lesquels Vernal ne communique pas de statut de connexion fiable. */
    unknownDevices: z.number().optional(),
    knownDevicesFound: z.array(z.string()),
    averageBattery: z.number().nullable(),
    lastUpdate: z.string().nullable(),
    devices: z.array(deviceSchema),
});
export const alertTypeSchema = z.enum([
    'stale_location',
    'low_battery',
    'geofence_enter',
    'geofence_exit',
    'refresh_failed',
    'suspicious_location',
    'invalid_coordinates',
    'provider_status_unavailable',
    /** Conservation fidèle d'un ancien événement dont le type n'existe plus. */
    'legacy_event',
]);
export const alertSeveritySchema = z.enum(['info', 'warning', 'critical']);
export const alertStatusSchema = z.enum(['new', 'read', 'resolved']);
export const dataModeSchema = z.enum(['mock', 'real']);
export const localAlertSchema = z.object({
    id: z.string(),
    deviceNum: z.string().nullable(),
    deviceName: z.string().nullable(),
    type: alertTypeSchema,
    severity: alertSeveritySchema,
    title: z.string(),
    message: z.string(),
    observedValue: z.unknown().nullable(),
    threshold: z.unknown().nullable(),
    source: z.string(),
    observedAt: z.string().nullable(),
    createdAt: z.string(),
    readAt: z.string().nullable(),
    resolvedAt: z.string().nullable(),
    status: alertStatusSchema,
    dataMode: dataModeSchema,
});
export const alertPositionStateSchema = z.object({
    deviceNum: z.string(),
    deviceName: z.string(),
    lastReliablePositionAt: z.string().nullable(),
    source: z.string().nullable(),
});
export const apiLogEntrySchema = z.object({
    id: z.string(),
    timestamp: z.string(),
    /** Endpoint amont Vernal, ex: /device/getDeviceList */
    endpoint: z.string(),
    method: z.string(),
    /** Route backend MyTag appelée par le client, ex: POST /api/devices */
    source: z.string().nullable(),
    statusCode: z.number().nullable(),
    /** Code métier renvoyé par l'API Vernal */
    apiCode: z.union([z.string(), z.number()]).nullable(),
    apiMessage: z.string().nullable(),
    durationMs: z.number(),
    mock: z.boolean(),
    success: z.boolean(),
    /** Payload requête assaini (secrets masqués) */
    request: z.unknown(),
    /** Payload réponse assaini (secrets masqués) */
    response: z.unknown(),
    error: z
        .object({
        code: z.string(),
        message: z.string(),
        details: z.unknown().optional(),
    })
        .nullable(),
});
export const cryptoDiagnosticsSchema = z.object({
    configured: z.boolean(),
    mode: z.string(),
    padding: z.string(),
    keySource: z.string(),
    keyFormat: z.string(),
    keyStrategy: z.string(),
    /** Longueur en octets de la clé dérivée (0 si absente) */
    derivedKeyLength: z.number(),
    keyPreview: z.string().nullable(),
    selfTest: z.object({
        ok: z.boolean(),
        message: z.string(),
    }),
});
//# sourceMappingURL=models.js.map